import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { TABS } from "../../routes/dashboard/personal-cabinet/constants";
import { apiPost, apiDelete, apiGet } from "../../services/api/apiClient";
import type { Customer } from "../../types/api";

type LoadingStatus = "idle" | "loading" | "succeeded" | "failed";

interface ProjectsState {
  projects: Customer[];
  fetchProjectsStatus: LoadingStatus;
  fetchProjectsError: string | null;
  paymentConfirmationDialogOpen: boolean;
  addAccountAndPayDialogOpen: boolean;
  selectedProject: Customer | null;
  isDrawerOpen: boolean;
  changes: Partial<Customer> | null;
  resetPasswordStatus: LoadingStatus;
  resetPasswordError: string | null;
  editingBillingAddress: boolean;
  updateProjectPaymentMethodStatus: LoadingStatus;
  updateProjectPaymentMethodError: string | null;
  deleteProjectPaymentMethodStatus: LoadingStatus;
  deleteProjectPaymentMethodError: string | null;
  updateProjectBillingAddressStatus: LoadingStatus;
  updateProjectBillingAddressError: string | null;
  updateProjectShippingAddressStatus: LoadingStatus;
  updateProjectShippingAddressError: string | null;
  activeTab: number;
}

const initialState: ProjectsState = {
  projects: [],
  fetchProjectsStatus: "idle",
  fetchProjectsError: null,

  paymentConfirmationDialogOpen: false,

  addAccountAndPayDialogOpen: false,

  selectedProject: null,
  isDrawerOpen: false,
  changes: null,

  resetPasswordStatus: "idle",
  resetPasswordError: null,

  editingBillingAddress: false,

  updateProjectPaymentMethodStatus: "idle",
  updateProjectPaymentMethodError: null,

  deleteProjectPaymentMethodStatus: "idle",
  deleteProjectPaymentMethodError: null,

  updateProjectBillingAddressStatus: "idle",
  updateProjectBillingAddressError: null,

  updateProjectShippingAddressStatus: "idle",
  updateProjectShippingAddressError: null,

  activeTab: TABS.INVOICES,
};

export const resetPasswordAuthorized = createAsyncThunk(
  "projects/resetPasswordAuthorized",
  async (data: any, { rejectWithValue }) => {
    const { newPassword } = data;

    try {
      const resp = await apiPost({
        endpoint: "/reset-password-authorized",
        data: { newPassword },
        withAuth: true,
      });

      return resp;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to reset password");
    }
  },
);

export const fetchProjects = createAsyncThunk(
  "projects/fetchProjects",
  async () => {
    const userId = JSON.parse(localStorage.getItem("user") || "null")?.id;

    // Token check is handled by apiClient with withAuth: true
    try {
      const data = await apiGet({
        endpoint: "/get-customers-by-user-id",
        params: { userId },
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      throw new Error(error.message || "Failed to fetch projects");
    }
  },
);

export const updateProjectPaymentMethod = createAsyncThunk(
  "projects/updateProjectPaymentMethod",
  async (
    {
      projectId,
      accountNumber,
      routingNumber,
      accountName,
      isSavings = false,
      isPersonalAcct = false,
      // Intuit requires a phone number on the account it stores. Both forms ask
      // for it and pass it through — the drawer in the object it builds, the
      // add-and-pay dialog by spreading its changes — but this is where the
      // request is assembled, and the field was not in the list: the number was
      // collected and dropped, and the API fell back to the customer's record,
      // which a client may never have filled in.
      phone,
    }: any,
    { rejectWithValue },
  ) => {
    try {
      const resp = await apiPost({
        endpoint: "/update-customer-payment-method",
        data: {
          accountNumber,
          routingNumber,
          accountName,
          isSavings,
          isPersonalAcct,
          ...(phone ? { phone } : {}),
        },
        params: { projectId },
        withAuth: true,
      });

      return resp;
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to update payment method",
      );
    }
  },
);

export const deleteProjectPaymentMethod = createAsyncThunk(
  "projects/deleteProjectPaymentMethod",
  async ({ accountId, customerId }: any, { rejectWithValue }) => {
    try {
      const resp = await apiDelete({
        endpoint: "/delete-customer-payment-method",
        data: { accountId, customerId },
        withAuth: true,
      });

      return resp;
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to delete payment method",
      );
    }
  },
);

export const updateBillingAddress = createAsyncThunk(
  "projects/updateBillingAddress",
  async ({ projectId, billingAddress }: any, { rejectWithValue }) => {
    try {
      const resp = await apiPost({
        endpoint: "/update-customer-billing-address",
        data: { ...billingAddress },
        params: { projectId },
        withAuth: true,
      });

      return resp;
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to update billing address",
      );
    }
  },
);

export const updateShippingAddress = createAsyncThunk(
  "projects/updateShippingAddress",
  async ({ projectId, lineItemId, shippingAddress }: any, { rejectWithValue }) => {
    try {
      const resp = await apiPost({
        endpoint: "/update-customer-shipping-address",
        data: { lineItemId, ...shippingAddress },
        params: { projectId },
        withAuth: true,
      });

      return resp;
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to update shipping address",
      );
    }
  },
);

const projectsSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    resetProjects: () => initialState,
    setActiveTab: (state: any, action: PayloadAction<any>) => {
      state.activeTab = action.payload;
    },
    selectProject: (state: any, action: PayloadAction<any>) => {
      state.selectedProject = action.payload;
    },
    selectProjectById: (state: any, action: PayloadAction<any>) => {
      state.selectedProject = state.projects?.find(
        (project: any) => project?.id === action.payload,
      );
    },
    openProjectDrawer: (state: any, action: PayloadAction<any>) => {
      state.selectedProject = action.payload;
      state.isDrawerOpen = true;
    },
    closeProjectDrawer: (state) => {
      state.isDrawerOpen = false;
      state.changes = null;
      state.resetPasswordStatus = "idle";
      state.resetPasswordError = null;
      state.updateProjectPaymentMethodStatus = "idle";
      state.updateProjectPaymentMethodError = null;
      state.deleteProjectPaymentMethodStatus = "idle";
      state.deleteProjectPaymentMethodError = null;
      state.updateProjectBillingAddressStatus = "idle";
      state.updateProjectBillingAddressError = null;
      state.updateProjectShippingAddressStatus = "idle";
      state.updateProjectShippingAddressError = null;
      state.editingBillingAddress = false;
    },
    changeProjectData: (state: any, action: PayloadAction<any>) => {
      const { name, value } = action.payload;

      state.changes = {
        ...state.changes,
        [name]: value,
      };
    },
    resetProjectsMessages: (state) => {
      state.resetPasswordStatus = "idle";
      state.resetPasswordError = null;
      state.updateProjectPaymentMethodStatus = "idle";
      state.updateProjectPaymentMethodError = null;
      state.deleteProjectPaymentMethodStatus = "idle";
      state.deleteProjectPaymentMethodError = null;
      state.updateProjectBillingAddressStatus = "idle";
      state.updateProjectBillingAddressError = null;
      state.updateProjectShippingAddressStatus = "idle";
      state.updateProjectShippingAddressError = null;
    },
    openAddAccountAndPayDialog: (state) => {
      state.addAccountAndPayDialogOpen = true;
    },
    closeAddAccountAndPayDialog: (state) => {
      state.addAccountAndPayDialogOpen = false;
    },
    openPaymentConfirmationDialog: (state) => {
      state.paymentConfirmationDialogOpen = true;
    },
    closePaymentConfirmationDialog: (state) => {
      state.paymentConfirmationDialogOpen = false;
    },
    setEditingBillingAddress: (state: any, action: PayloadAction<any>) => {
      state.editingBillingAddress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetPasswordAuthorized.pending, (state) => {
        state.resetPasswordStatus = "loading";
        state.resetPasswordError = null;
      })
      .addCase(resetPasswordAuthorized.fulfilled, (state) => {
        state.resetPasswordStatus = "succeeded";
        state.resetPasswordError = null;
      })
      .addCase(resetPasswordAuthorized.rejected, (state: any, action: PayloadAction<any>) => {
        state.resetPasswordStatus = "failed";
        state.resetPasswordError = action.payload;
      })
      .addCase(fetchProjects.pending, (state) => {
        state.fetchProjectsStatus = "loading";
      })
      .addCase(fetchProjects.fulfilled, (state: any, action: PayloadAction<any>) => {
        state.fetchProjectsStatus = "succeeded";
        state.projects = action.payload ?? [];
        // Auto-select first project when none is selected yet
        if (!state.selectedProject && action.payload?.length > 0) {
          state.selectedProject = action.payload[0];
        }
      })
      .addCase(fetchProjects.rejected, (state: any, action: PayloadAction<any>) => {
        state.fetchProjectsStatus = "failed";
        state.fetchProjectsError = action.payload;
      })
      .addCase(updateProjectPaymentMethod.pending, (state) => {
        state.updateProjectPaymentMethodStatus = "loading";
      })
      .addCase(updateProjectPaymentMethod.fulfilled, (state) => {
        state.updateProjectPaymentMethodStatus = "succeeded";
        state.updateProjectPaymentMethodError = null;
        state.changes = null;
      })
      .addCase(updateProjectPaymentMethod.rejected, (state: any, action: PayloadAction<any>) => {
        state.updateProjectPaymentMethodStatus = "failed";
        state.updateProjectPaymentMethodError = action.payload;
      })
      .addCase(deleteProjectPaymentMethod.pending, (state) => {
        state.deleteProjectPaymentMethodStatus = "loading";
      })
      .addCase(deleteProjectPaymentMethod.fulfilled, (state) => {
        state.deleteProjectPaymentMethodStatus = "succeeded";
        state.deleteProjectPaymentMethodError = null;
      })
      .addCase(deleteProjectPaymentMethod.rejected, (state: any, action: PayloadAction<any>) => {
        state.deleteProjectPaymentMethodStatus = "failed";
        state.deleteProjectPaymentMethodError = action.payload;
      })
      .addCase(updateBillingAddress.pending, (state) => {
        state.updateProjectBillingAddressStatus = "loading";
      })
      .addCase(updateBillingAddress.fulfilled, (state) => {
        state.updateProjectBillingAddressStatus = "succeeded";
        state.updateProjectBillingAddressError = null;
      })
      .addCase(updateBillingAddress.rejected, (state: any, action: PayloadAction<any>) => {
        state.updateProjectBillingAddressStatus = "failed";
        state.updateProjectBillingAddressError = action.payload;
      })
      .addCase(updateShippingAddress.pending, (state) => {
        state.updateProjectShippingAddressStatus = "loading";
      })
      .addCase(updateShippingAddress.fulfilled, (state) => {
        state.updateProjectShippingAddressStatus = "succeeded";
        state.updateProjectShippingAddressError = null;
      })
      .addCase(updateShippingAddress.rejected, (state: any, action: PayloadAction<any>) => {
        state.updateProjectShippingAddressStatus = "failed";
        state.updateProjectShippingAddressError = action.payload;
      });
  },
});

export const {
  resetProjects,
  setActiveTab,
  selectProject,
  selectProjectById,
  openProjectDrawer,
  closeProjectDrawer,
  changeProjectData,
  resetProjectsMessages,
  openAddAccountAndPayDialog,
  closeAddAccountAndPayDialog,
  openPaymentConfirmationDialog,
  closePaymentConfirmationDialog,
  setEditingBillingAddress,
} = projectsSlice.actions;

export default projectsSlice.reducer;
